import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import { trackCTA } from "../../lib/analytics";
import { buildDemoInvitationUrl } from "../../lib/demoInvitation.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Page hero. Same shape as the photo-sharing hero — eyebrow, H1 at
 * `heading-9vw`, supporting line, two actions — so the two SEO pages read as
 * chapters of one story rather than two designs.
 *
 * Two deliberate differences:
 *
 *  - The primary CTA points at the plans rather than the homepage. This page's
 *    entire subject is Heirloom-only, so the useful next step is the price, not
 *    a second tour of the brand.
 *  - The secondary CTA is the live sample invitation, which is the strongest
 *    asset the page has. It opens in a new tab because it is a different
 *    origin (invite.agapae.gr) and losing this page behind it would be a poor
 *    trade for a visitor who arrived from search.
 *
 * No `data-w-id` and no inline `opacity: 0`, for the reason documented on the
 * photo-sharing hero: the H1 must never depend on the Webflow interaction
 * engine to become visible.
 */
const InvitationHero = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  return (
    <header className="section wps-hero">
      <div className="container">
        <p className="wps-eyebrow">
          {t("digitalWeddingInvitation.hero.eyebrow")}
        </p>
        <h1 className="heading-9vw wps-hero-heading">
          {t("digitalWeddingInvitation.hero.heading")}
        </h1>
        <p className="body-20 wps-hero-body">
          {t("digitalWeddingInvitation.hero.body")}
        </p>
        <div className="wps-hero-actions">
          <Button
            href={localize("/#pricing")}
            text={t("digitalWeddingInvitation.hero.ctaPrimary")}
            className="button-lg"
            onClick={(e) => {
              trackCTA("digital_wedding_invitation_hero", {
                ...analyticsContext,
                ctaName: "see_plans",
              });
              scrollToPlans(e);
            }}
          />
          <a
            className="wps-text-link"
            href={buildDemoInvitationUrl(shortLanguage)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackCTA("digital_wedding_invitation_hero", {
                ...analyticsContext,
                ctaName: "view_demo",
              })
            }
          >
            {t("digitalWeddingInvitation.hero.ctaSecondary")}
          </a>
        </div>
      </div>
    </header>
  );
};

export default InvitationHero;
